import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react'
import type { ReactNode } from 'react'
import { api, ErroApi, SESSAO_MORTA, type Contexto } from './api'
export type { Clinica } from './api'

/**
 * Sessão do usuário.
 *
 * O front NUNCA guarda token. O cookie é `__Host-`, httpOnly, e este
 * contexto só sabe o que a API respondeu em `/v1/me/contexto`. Se a
 * sessão morrer no servidor, a próxima chamada devolve 401 e o app
 * volta ao login — não existe estado local que discorde do servidor.
 */

type Estado =
  | { fase: 'carregando' }
  | { fase: 'fora' }
  | { fase: 'dentro'; ctx: Contexto }

type Sessao = {
  estado: Estado
  entrar(email: string, senha: string): Promise<void>
  sair(): Promise<void>
  escolherClinica(tenantId: string): Promise<void>
  recarregar(): Promise<void>
  /** `*` (dono) cobre tudo. */
  pode(escopo: string): boolean
}

const Ctx = createContext<Sessao | null>(null)

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' })

  const recarregar = useCallback(async () => {
    try {
      const ctx = await api<Contexto>('/v1/me/contexto')
      setEstado({ fase: 'dentro', ctx })
    } catch (e) {
      if (e instanceof ErroApi && e.status === 401) setEstado({ fase: 'fora' })
      else setEstado({ fase: 'fora' })
    }
  }, [])

  useEffect(() => { void recarregar() }, [recarregar])

  // Qualquer 401 em qualquer chamada derruba a sessão aqui. Sem isto,
  // a pessoa continua clicando numa tela que já não tem acesso.
  useEffect(() => {
    const aoMorrer = () => setEstado({ fase: 'fora' })
    window.addEventListener(SESSAO_MORTA, aoMorrer)
    return () => window.removeEventListener(SESSAO_MORTA, aoMorrer)
  }, [])

  const entrar = useCallback(async (email: string, senha: string) => {
    await api('/v1/auth/login', { metodo: 'POST', corpo: { email, senha } })
    await recarregar()
  }, [recarregar])

  const sair = useCallback(async () => {
    try { await api('/v1/auth/logout', { metodo: 'POST' }) } finally {
      setEstado({ fase: 'fora' })
    }
  }, [])

  const escolherClinica = useCallback(async (tenantId: string) => {
    await api('/v1/me/clinica', { metodo: 'POST', corpo: { tenantId } })
    await recarregar()
  }, [recarregar])

  const pode = useCallback((escopo: string) => {
    if (estado.fase !== 'dentro') return false
    const e = estado.ctx.escopos ?? []
    return e.includes('*') || e.includes(escopo)
  }, [estado])

  const valor = useMemo<Sessao>(
    () => ({ estado, entrar, sair, escolherClinica, recarregar, pode }),
    [estado, entrar, sair, escolherClinica, recarregar, pode],
  )

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>
}

export function useSessao() {
  const s = useContext(Ctx)
  if (!s) throw new Error('useSessao fora do ProvedorSessao')
  return s
}

/** Atalho para as telas que só existem com clínica escolhida. */
export function useClinica() {
  const { estado } = useSessao()
  if (estado.fase !== 'dentro' || !estado.ctx.tenantId) return null
  return estado.ctx
}
